import React, { useEffect, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Card, CardContent, CardHeader, createStyles, Divider, Grid, List, makeStyles, Theme } from '../index';
import { DraggableListItemContainer } from './FluentTemplateListComponents/DraggableListItemContainer';
import { DroppableListItem } from './FluentTemplateListComponents/DroppableListItem';
import { CloudUpload } from '@material-ui/icons';
import { FilterMenu } from './FluentTemplateListComponents/FilterMenu/FilterMenu';
import { FilterChips } from './FluentTemplateListComponents/FilterMenu/FilterChips';
import { FluentTransferListProps } from './FluentTemplateListComponents/models/transferListProps.types';
import {
  createFilterWithValueType,
  filterBothSearchbarAndFilters,
  filterOptionWithValue,
  searchElement,
} from './FluentTemplateListComponents/utils/transferList.utils';
import { NoResults } from './FluentTemplateListComponents/FilterMenu/NoResults';
import { DropId } from './FluentTemplateListComponents/models/transferList.enums';
import { CurrentFilterType, ItemType } from './FluentTemplateListComponents/models/transferList.types';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    drawerContentContainer: {
      minHeight: '100%',
      maxHeight: '100%',
      height: '100%',
    },
    roleCard: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: theme.palette.background.paper,
      minHeight: '100%',
      maxHeight: '100%',
    },
    rolesAddedContainer: {
      padding: theme.spacing(0, 2, 2, 2),
      display: 'flex',
      flexGrow: 1,
      overflowY: 'auto',
    },
    placeholderCard: {
      display: 'flex',
      borderStyle: 'dashed',
      justifyContent: 'center',
      alignItems: 'center',
      '& div': {
        display: 'inline-block',
        fontWeight: 'bold',
        color: theme.palette.text.secondary,
      },
      '& div:first-child': {
        paddingTop: theme.spacing(0.4),
        paddingRight: theme.spacing(1),
      },
      margin: theme.spacing(2),
      padding: theme.spacing(2),
    },
    list: {
      paddingTop: theme.spacing(0),
      flexGrow: 1,
    },
    rolesAddedDropContainer: {
      height: '100%',
    },
  }),
);

export const FluentTransferList = ({
  addedItems = [],
  item,
  onDragEnd,
  addItem,
  removeItem,
  isAdded,
  draggableItemListHeader,
  droppableHelperText,
  droppableItemListHeader,
  showSearchbar = false,
  showFilter = false,
  searchbarPlaceholder = 'Search',
  filterOptions = [],
  resetButtonValue,
  defaultFilters = [],
  addMultiples = false,
  itemIcon,
}: FluentTransferListProps): JSX.Element => {
  const [filteredItems, setFilteredItems] = useState(item);
  const [formData, setFormData] = useState('');
  const [currentFilters, setCurrentFilters] = useState<CurrentFilterType[]>(createFilterWithValueType(defaultFilters));
  useEffect(() => {
    const combineFilterAndSearch = () => {
      return filterBothSearchbarAndFilters(searchElement(item, formData), currentFilters);
    };

    const comparer = (otherArray: any[]) => {
      return (current: any) => {
        return (
          otherArray.filter((other: ItemType) => {
            return other.title == current.title;
          }).length == 0
        );
      };
    };

    setFilteredItems(combineFilterAndSearch());

    if (!addMultiples) {
      const filteredListOfAddedItems = item.filter(comparer(addedItems));

      const duplicates = combineFilterAndSearch().filter((object) => {
        if (filteredListOfAddedItems.some((e) => e.title === object.title)) {
          return true;
        }
        return false;
      });

      setFilteredItems(duplicates);
    }
  }, [item, currentFilters, addedItems, formData]);

  const onChange = (event: React.SetStateAction<string>) => {
    setFormData(event);
  };

  const classes = useStyles();

  return (
    <div data-testid="FluentTransferList-component">
      <DragDropContext onDragEnd={onDragEnd}>
        {showFilter && currentFilters.length > 0 ? (
          <FilterChips
            currentFilters={currentFilters}
            setCurrentFilters={(filter: CurrentFilterType[]) => setCurrentFilters(filter)}
          />
        ) : null}

        <Grid className={classes.drawerContentContainer} container>
          <Grid item xs={12} md={6} style={{ order: 1 }}>
            <Card variant={'outlined'}>
              <CardHeader
                title={draggableItemListHeader}
                action={
                  <FilterMenu
                    activeSearchFilter={formData}
                    setSearchFilter={onChange}
                    setCurrentFilters={setCurrentFilters}
                    currentFilters={currentFilters}
                    showFilter={showFilter}
                    showSearchbar={showSearchbar}
                    searchbarPlaceholder={searchbarPlaceholder}
                    filterOptions={filterOptionWithValue(filterOptions)}
                    resetButtonValue={resetButtonValue}
                  />
                }
              />
              <Divider />
              <CardContent style={{ paddingTop: 0 }}>
                <List className={classes.list} dense={false}>
                  <Droppable droppableId={DropId.AVAILABLE} isDropDisabled={true}>
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef}>
                        {filteredItems.length > 0 ? (
                          filteredItems.map((item: ItemType, index) => (
                            <DraggableListItemContainer
                              key={index}
                              item={item}
                              addItem={addItem}
                              index={index}
                              isAdded={isAdded}
                              itemIcon={itemIcon}
                            />
                          ))
                        ) : (
                          <NoResults />
                        )}
                      </div>
                    )}
                  </Droppable>
                </List>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6} style={{ order: 2 }}>
            <Card variant={'outlined'} className={classes.roleCard}>
              <CardHeader title={droppableItemListHeader} />
              <Divider />
              <CardContent className={classes.rolesAddedContainer}>
                <List className={classes.list} dense={false}>
                  <Droppable droppableId={DropId.ADDED} isDropDisabled={false}>
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className={classes.rolesAddedDropContainer}
                      >
                        <Card className={classes.placeholderCard} variant={'outlined'}>
                          <div>
                            <CloudUpload />
                          </div>
                          <div>{droppableHelperText}</div>
                        </Card>

                        {addedItems?.map((item: ItemType, index) => {
                          return (
                            <Draggable key={index} draggableId={index.toString()} index={index}>
                              {(provided) => (
                                <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                                  <DroppableListItem
                                    index={index}
                                    item={item.title}
                                    removeItem={removeItem}
                                    itemIcon={itemIcon}
                                  />
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DragDropContext>
    </div>
  );
};

export default FluentTransferList;
